import React from "react";
import { graphql, useStaticQuery, Link } from "gatsby";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { getImage, GatsbyImage } from "gatsby-plugin-image";
import AdviceArticleCarousel from "../components/AdviceArticleCarousel";
import { getBlogPath } from "../utils/blogRoutes";
import "../../scss/main.scss";

const CATEGORY_CONFIG = [
	{
		slug: "starting-the-conversation",
		name: "Starting the conversation",
		order: [
			"When is care needed?",
			"Discussing care options as a family",
			"What to Do When a Parent Refuses Care",
			"When Your Parent Doesn't Want a Stranger in the House",
			"Live-in care vs care home",
			"Is Live-in Care Only for Dementia? Clearing Up a Common Assumption",
			"What can a live-in carer help with?",
			"Live-in Care Company vs Matching Agency: What's the Difference?",
			"Live-in Care Explained: How It Compares to a Nanny or Au Pair",
			"Balancing Your Own Life While Caring for an Ageing Parent",
			"When Both Parents Need Care at the Same Time",
			"Grandparents Raising Grandchildren While Managing Their Own Care Needs",
			"How to find the right carer for you",
			"What is a care consultation?",
			"What is a care assessment?",
			"How quickly can live-in care start?",
			"Is night-time care included in live-in care?",
			"What is the minimum cover for care?",
		],
	},
	{
		slug: "paying-for-care",
		name: "Paying for care",
		order: [
			"Cost of live-in care vs care home",
			"What Is the Cost of Live-in Care",
			"What are my funding options?",
			"Self-funding care",
			"State funding (CHC)",
			"Local authority funding",
			"How Do Payments Work at Edyn",
		],
	},
	{
		slug: "getting-care-in-place",
		name: "Getting Care in place",
		order: [
			"How to choose the right live-in care provider",
			"How Edyn Works: From First Call to Carer Match",
			"CQC vs Introductory Agency",
			"Is Edyn CQC registered, and what does that mean?",
			"What makes Edyn different?",
			"Which one of Edyn's packages fits your situation?",
			"What If I'm Not Happy With My Carer",
			"Live-in Care for Younger Adults With Disabilities or Chronic Illness",
			"Live-in Care for a Loved One With Sight or Hearing Loss",
			"Live-in Care for Multi-Generational and Multicultural Households",
			"How does Edyn recruit its carers?",
			"How does Edyn ensure quality?",
			"The Professional Standards Our Carers Uphold",
			"Does Live-in Care Cover Rural Areas?",
			"Live-in Care in Rural Areas vs Cities: What's Different?",
			"Where does Edyn operate?",
			"Not Comfortable With Technology? Here's How We Make Care Work for You",
			"Edyn's Software",
			"Best Home Care Employer (London) at the Great British Care Awards 2025",
		],
	},
	{
		slug: "life-with-a-live-in-carer",
		name: "Life With a Live-in Carer",
		order: [
			"What Happens in the First Week of Live-in Care",
			"A Day in the Life With a Live-in Carer",
			"What Happens If Your Live-in Carer Is Unwell or Needs Cover?",
			"What Does a Live-in Carer Need in Your Home?",
			"Keeping a Parent's Routines and Rituals Alive",
			"Pets and Live-in Care: Keeping the Whole Household Comfortable",
			"Do I Need to Provide Food for My Carer?",
			"Can a Live-in Carer Also Help My Spouse?",
			"Can I Have a Carer Who Speaks My Loved One's Language?",
			"Can a Live-in Carer Drive My Loved One?",
			"Helping a Parent Stay Engaged in Their Community With Live-in Support",
			"How Do Edyn's Carers Handle Food Safety?",
			"Why Live-in Carers Need Breaks",
			"Why Our Carers Can't Accept Gifts (and How to Say Thank You)",
			"Home Surveillance and Care: The Benefits, and What to Be Conscious Of",
			"How to make a home safe for the elderly",
			"Keeping Elderly Relatives Socially Connected Over the Holidays (seasonal)",
		],
	},
	{
		slug: "safety-health-and-wellbeing",
		name: "Safety, Health & Wellbeing",
		order: [
			"What Happens If There Is an Accident",
			"How Do Our Carers Handle Medication Safely",
			"UTIs and Sudden Confusion in Older Adults: Why It Is Often Mistaken for Dementia",
			"Mental Capacity",
			"Moving and Handling",
			"Dehydration in Older Adults: A Commonly Missed Risk at Home",
			"How Do We Keep Your Loved One's Money Safe",
			"Companionship as Care: Why Loneliness Is a Health Issue Too",
			"Sleep Problems in Older Adults: Causes and Home-Based Solutions",
			"End-of-life care",
			"Long-Distance Caregiving: Staying Involved When You Live Far Away",
			"Heatwave Safety for Older Adults",
			"Winter Safety for Older Adults",
		],
	},
	{
		slug: "hospital-and-care-transitions",
		name: "Hospital & Care Transitions",
		order: [
			"What Happens When Your Loved One Goes Into Hospital?",
			"What Happens During a Hospital/Care Home Discharge",
			"Rehabilitation Care at Home: Support for Recovery After Hospital",
		],
	},
	{
		slug: "live-in-care-by-region",
		name: "Live-in Care by Region",
		order: [
			"Live-in Care in London",
			"Live-in Care in the South East",
			"Live-in Care in the South West",
			"Live-in Care in the East Midlands",
			"Live-in Care in the West Midlands",
			"Live-in Care in the North East",
			"Live-in Care in the North West",
			"Live-in Care in Yorkshire and The Humber",
			"Live-in Care in the East of England",
		],
	},
];

const ORDER_TITLE_ALIASES = {
	"when is care needed": [
		"When is care needed? How to spot the signs, and what to do next",
	],
	"live in care vs care home": [
		"Live-in care vs. care home: Which option is right?",
		"Live-in care vs. Care Home",
	],
	"what is the minimum cover for care": [
		"What Is the Minimum Cover Time for Live-in Care?",
	],
	"cost of live in care vs care home": [
		"Cost of a Care Home vs Live-in Care: The Surprising Truth",
	],
	"what is the cost of live in care": [
		"What Is the Cost of Live-in Care?",
		"What is the cost of live-in care?",
	],
	"what are my funding options": ["What Are My Funding Options for Care?"],
	"self funding care": [
		"Self-Funding Care: Your Options Explained",
		"Self-funding care",
	],
	"state funding chc": [
		"A Guide to State-Funded Care",
		"State and local authority funding",
	],
	"local authority funding": ["Local Authority Funding for Care"],
	"how do payments work at edyn": ["How Do Payments Work at Edyn?"],
	"what makes edyn different": [
		"Timeless Care, Modern Thinking: What Makes Edyn Different",
	],
	"which one of edyns packages fits your situation": [
		"Which of Edyn's Packages Fits Your Situation?",
	],
	"what if im not happy with my carer": [
		"What If I'm Not Happy With My Carer?",
	],
	"not comfortable with technology heres how we make care work for you": [
		"Not Comfortable With Technology? Here's How We Make Care Work for You",
	],
	"edyns software": ["Technology That Makes Care More Human"],
	"how do our carers handle medication safely": [
		"How Do Our Carers Handle Medication Safely?",
	],
	"how do we keep your loved ones money safe": [
		"How Do We Keep Your Loved One's Money Safe?",
	],
	"mental capacity": [
		"Mental Capacity: How Our Carers Respect Your Loved One's Right to Choose",
	],
	"moving and handling": [
		"Moving and Handling: How Our Carers Keep Your Loved One Safe and Mobile",
	],
	"end of life care": ["End-of-Life Care at Home"],
	"what happens during a hospital care home discharge": [
		"What Happens When Your Loved One Is Discharged From Hospital or a Care Home?",
	],
	"live in care in yorkshire and the humber": [
		"Live-in Care in Yorkshire and the Humber",
	],
};

const truncateExcerpt = (excerpt) => {
	if (!excerpt || excerpt.length <= 220) {
		return excerpt;
	}

	const cutoffIndex = excerpt.indexOf(" ", 220);
	return cutoffIndex === -1
		? `${excerpt} ...`
		: `${excerpt.slice(0, cutoffIndex)} ...`;
};

const normalizeTitle = (value = "") =>
	value
		.toLowerCase()
		.replace(/&/g, " and ")
		.replace(/[^a-z0-9\s]/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const titleMatchesRule = (title, rule) => {
	const normalizedTitle = normalizeTitle(title);
	const normalizedRule = normalizeTitle(rule);

	if (!normalizedTitle || !normalizedRule) {
		return false;
	}

	return (
		normalizedTitle === normalizedRule ||
		normalizedTitle.includes(normalizedRule) ||
		normalizedRule.includes(normalizedTitle)
	);
};

const getOrderRank = (title, orderRules) => {
	for (let index = 0; index < orderRules.length; index += 1) {
		const rule = orderRules[index];
		const possibleTitles = [
			rule,
			...(ORDER_TITLE_ALIASES[normalizeTitle(rule)] || []),
		];

		if (
			possibleTitles.some((possibleTitle) =>
				titleMatchesRule(title, possibleTitle)
			)
		) {
			return index;
		}
	}

	return Number.MAX_SAFE_INTEGER;
};

const sortPostsByConfiguredOrder = (posts, orderRules) =>
	posts
		.map((post, index) => ({
			post,
			index,
			rank: getOrderRank(post.title, orderRules),
		}))
		.sort((left, right) => {
			if (left.rank !== right.rank) {
				return left.rank - right.rank;
			}

			return left.index - right.index;
		})
		.map(({ post }) => post);

export default function Blog() {
	const {
		allGhostPost: { nodes: blogPosts },
	} = useStaticQuery(
		graphql`
			query {
				allGhostPost(sort: { order: DESC, fields: [published_at] }) {
					nodes {
						title
						slug
						excerpt
						published_at(formatString: "DD MMM YYYY")
						feature_image_sharp {
							childImageSharp {
								gatsbyImageData
							}
						}
						primary_tag {
							name
							slug
						}
					}
				}
			}
		`
	);

	const categorySections = blogPosts.reduce((sections, post) => {
		const tag = post.primary_tag;

		if (tag && tag.slug === "test") {
			return sections;
		}
		const categorySlug = tag ? tag.slug : "additional-care-guides";
		const categoryName = tag ? tag.name : "Additional care guides";

		const existingSection = sections.find(
			(section) => section.slug === categorySlug
		);

		if (existingSection) {
			existingSection.posts.push(post);
			return sections;
		}

		sections.push({
			name: categoryName,
			slug: categorySlug,
			posts: [post],
		});

		return sections;
	}, []);

	const orderedCategorySections = [
		...CATEGORY_CONFIG.map(({ slug, name, order }) => {
			const matchingSection = categorySections.find(
				(section) => section.slug === slug
			);

			if (!matchingSection) {
				return null;
			}

			return {
				...matchingSection,
				name,
				posts: sortPostsByConfiguredOrder(matchingSection.posts, order),
			};
		}).filter(Boolean),
		...categorySections.filter(
			({ slug }) => !CATEGORY_CONFIG.some((section) => section.slug === slug)
		),
	];
	const latestPost = blogPosts[0];
	const popularTopics = orderedCategorySections.slice(0, 4);
	const heroArticles = [
		{
			label: "When is care needed?",
			post: orderedCategorySections.find(
				({ slug }) => slug === "starting-the-conversation"
			)?.posts[0],
		},
		{
			label: "What will it cost?",
			post: orderedCategorySections.find(
				({ slug }) => slug === "paying-for-care"
			)?.posts[0],
		},
		{
			label: "What does life with a carer feel like?",
			post:
				orderedCategorySections
					.find(({ slug }) => slug === "life-with-a-live-in-carer")
					?.posts.find(({ title }) =>
						normalizeTitle(title).includes("a day in the life")
					) ||
				orderedCategorySections.find(
					({ slug }) => slug === "life-with-a-live-in-carer"
				)?.posts[0],
		},
	].filter(({ post }) => post);

	return (
		<>
			<Seo
				title="Live-in Care Advice and Planning Guides | Edyn Care"
				description="Clear, practical guidance for families considering live-in care, including costs, funding, care assessments, choosing a provider and life with a carer."
				path="/blog/"
			/>
			<div className="container edyn-commercial-shell edyn-advice-shell">
				<Header />
				<main id="main-content" className="blog-landing-page">
					<section className="advice-hero">
						<div className="site-shell advice-hero__grid">
							<div className="advice-hero__copy">
								<p className="eyebrow eyebrow--light">Edyn advice</p>
								<h1>
									Care is easier to navigate when you do not have to figure it
									out alone.
								</h1>
								<p>
									Clear, practical guides for the decisions families face when
									life at home is changing.
								</p>
								<div className="button-row">
									<a className="button button--cream" href="#advice-topics">
										Browse advice
									</a>
									<Link className="button button--text-light" to="/care-consultation/">
										Talk to a Care Advisor{" "}
										<span aria-hidden="true">&#8594;</span>
									</Link>
								</div>
							</div>
							<div className="advice-hero__aside">
								<p>Start with the question that is on your mind.</p>
								<ul>
									{heroArticles.map(({ label, post }) => (
										<li key={post.slug}>
											<Link to={getBlogPath(post.slug)}>
												{label}
												<span aria-hidden="true">&#8594;</span>
											</Link>
										</li>
									))}
								</ul>
								<span className="advice-hero__mark" aria-hidden="true">
									?
								</span>
							</div>
						</div>
					</section>

					{latestPost && (
						<section
							className="advice-featured"
							aria-labelledby="latest-guide-title"
						>
							<div className="site-shell advice-featured__grid">
								<div className="advice-featured__copy">
									<p className="eyebrow">Latest guide</p>
									<h2 id="latest-guide-title">
										A little clarity, for the next step.
									</h2>
									<p>
										Our newest practical guide, written for families making
										decisions with care and confidence.
									</p>
									<Link
										className="button button--primary"
										to={getBlogPath(latestPost.slug)}
									>
										Read the latest guide
									</Link>
								</div>
								<Link
									to={getBlogPath(latestPost.slug)}
									className="advice-featured__article"
								>
									<div className="advice-featured__image">
										{latestPost.feature_image_sharp ? (
											<GatsbyImage
												placeholder="blurred"
												image={getImage(latestPost.feature_image_sharp)}
												alt=""
											/>
										) : null}
									</div>
									<div>
										<p className="advice-featured__date">
											{latestPost.published_at}
										</p>
										<h3>{latestPost.title}</h3>
										{latestPost.excerpt && (
											<p>{truncateExcerpt(latestPost.excerpt)}</p>
										)}
										<span>
											Read guide <span aria-hidden="true">&#8594;</span>
										</span>
									</div>
								</Link>
							</div>
						</section>
					)}

					<section
						id="advice-topics"
						className="advice-topic-index"
						aria-labelledby="advice-topics-title"
					>
						<div className="site-shell">
							<div className="advice-section-heading">
								<p className="eyebrow">Explore by topic</p>
								<h2 id="advice-topics-title">
									Advice for every stage of the conversation.
								</h2>
							</div>
							<div className="advice-topic-index__grid">
								{popularTopics.map(({ name, slug, posts }, index) => (
									<a key={slug} href={`#advice-${slug}`}>
										<span>{String(index + 1).padStart(2, "0")}</span>
										<strong>{name}</strong>
										<small>{posts.length} guides</small>
										<i aria-hidden="true">&#8595;</i>
									</a>
								))}
							</div>
						</div>
					</section>

					<section id="advice-conversation" className="advice-human-note">
						<div className="site-shell advice-human-note__inner">
							<div className="advice-human-note__portraits" aria-hidden="true">
								<img
									src="/assets/marketing-site/images/team/matthew.png"
									alt=""
								/>
								<img
									src="/assets/marketing-site/images/team/daniel-new.png"
									alt=""
								/>
								<img
									src="/assets/marketing-site/images/team/andrew.png"
									alt=""
								/>
							</div>
							<div className="advice-human-note__content">
								<p className="eyebrow eyebrow--light">A human conversation</p>
								<h2>Not sure which question to ask first?</h2>
								<p>
									Our Care Advisors can listen to what is changing, explain the
									options and help you find a calm way forward.
								</p>
								<Link className="button button--cream" to="/care-consultation/">
									Talk to a Care Advisor
								</Link>
							</div>
						</div>
					</section>

					<div className="advice-topics">
						{orderedCategorySections.map(({ name, slug, posts }, index) => (
							<section
								id={`advice-${slug}`}
								className="advice-topic"
								key={slug}
							>
								<div className="site-shell">
									<div className="advice-topic__heading">
										<p className="eyebrow">
											Topic {String(index + 1).padStart(2, "0")}
										</p>
										<h2>{name}</h2>
									</div>
									<AdviceArticleCarousel posts={posts} />
								</div>
							</section>
						))}
					</div>
					<section className="advice-archive section-space" aria-labelledby="all-guides-title">
						<div className="site-shell">
							<div className="advice-section-heading">
								<p className="eyebrow">Complete advice library</p>
								<h2 id="all-guides-title">All live-in care guides.</h2>
								<p>Every Edyn guide, grouped by topic and available from one crawlable index.</p>
							</div>
							<div className="advice-archive__grid">
								{orderedCategorySections.map(({ name, slug, posts }) => (
									<section key={slug} aria-labelledby={`archive-${slug}`}>
										<h3 id={`archive-${slug}`}>{name}</h3>
										<ul>
											{posts.map((post) => (
												<li key={post.slug}><Link to={getBlogPath(post.slug)}>{post.title}</Link></li>
											))}
										</ul>
									</section>
								))}
							</div>
						</div>
					</section>
					<Footer />
				</main>
			</div>
		</>
	);
}
